import { CurrencyPipe } from '@angular/common';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, combineLatest } from 'rxjs';
import { filter, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Customers } from '../../../account-info-header/models/AccountInfoApiResponse';
import { selectCreateWireTemplateCallSuccessful, selectEAuthCustomers, selectInstructions, selectRegType, selectSubAccountId } from '../../../account-info-header/reducers';
import { selectHasAccessInitiateMoneyMovement, selectHasAccessReleaseMoneyMovement, selectHasPositionLotsPhase2 } from '../../../authorize/reducers';
import { selectSendEmailErrorState } from '../../../setup-eAuthorization/reducers';
import { AdobeConstants, Template } from '../../../shared/adobe-tagging/AdobeConstants';
import { AdobeHelpers } from '../../../shared/adobe-tagging/adobeHelper';
import { EAuthInfo } from '../../../shared/models/EAuthInfo';
import { CamelCasePipe } from '../../../shared/pipes/CamelCasePipe';
import { RemoveHyphenFromAccountPipe } from '../../../shared/pipes/remove-hyphen-from-account.pipe';
import { selectEAuthInfo, selectIsCreatePdfFlow, selectPdf, selectPdfs } from '../../../shared/reducers';
import { FeatureFlagService } from '../../../shared/services/feature-flag-service';
import { Constants, confirmationPageButtons, digitalRegTypes, eAuthNotifications, navRouteConstants, pendReasonsStrings, positionConstants, submitAndCreateConfirmationMessages, transactionDetailLabels, transactionTypes, transactionTypesDict, transferTypes, yesNo } from '../../../shared/utilities/Constants';
import { getArrayBufferFromBase64, openPdfInNewWindow } from '../../../shared/utilities/pdf';
import { ConfirmationPageActions } from '../../actions';
import { ChecksExtraPrompts, ItExtraPrompts, ItToAccountDetails, TransactionForm, WireExtraPrompts, WireTransactionDetails, isCheckTransactionForm, isInternalTransferForm, isWireForm } from '../../models/form/TransactionForm';
import { InternalTransferListAcknowledgeResponseById, InternalTransfersInfo, ToAccountInfoById } from '../../models/internal-transfer/InternalTransfersInfo';
import { SubmitAndCreateStatus } from '../../models/shared/SubmitAndCreateStatus';
import { positionPage } from '../../../shared/utilities/PrintPageUtils';
import {
  selectAchStandingAuthId,
  selectCanRelease,
  selectConfirmationEAuthInfo,
  selectHasSubmitAndCreateError,
  selectInternalTransfersInfo,
  selectIs401kToAccount,
  selectIsBundled,
  selectIsCreateAchProfile,
  selectIsOneTimeTotalDist,
  selectAchRecurringFrequency,
  selectIsPending,
  selectIsUseStandingAuth,
  selectMappedPendReason,
  selectMultipleToAccountsPositionLotTableData,
  selectMultipleToJournalsList,
  selectPendForRelease,
  selectPendForReleaseCustomRule,
  selectIsSubmitAndCreate,
  selectStagedTransactionForm,
  selectStagedTransactionType,
  selectStagedUseTemplateVal,
  selectToAccountsInfo,
  selectTransferType,
  selectWireInfo
} from '../../reducers';
import { selectIsCreateSloaToggleFlagEnabled, selectIsEnableWiresCondensedLayout } from '../../../feature-flags/reducers';
import { InternalTransferVerifyResponse } from '../../models/internal-transfer/InternalTransferVerifyResponse';
import { mapInitiatedDraftsToTableColumn } from '../transactions-table/table-helpers';
import { selectInitReleaseTransactionInContext } from '../../../initiate-and-release/reducers';
import { DraftListTableColumn } from '../../../initiate-and-release/Models/Drafts/DraftResponse';
import { selectStandingAuthWireInfo } from '../../../standing-authorizations/reducers';

@Component({
  standalone: false,
  selector: 'mm-confirmation-page',
  templateUrl: './confirmation-page.component.html',
  styleUrls: ['./confirmation-page.component.scss'],
})
export class ConfirmationPageComponent implements OnInit {
  @Output() saveAsTemplateClicked = new EventEmitter<boolean>();
  @Output() requestSuccessful = new EventEmitter<void>();
  camelCasePipe = new CamelCasePipe;
  hyphenPipe = new RemoveHyphenFromAccountPipe();
  pdf: string;
  useSloa: boolean;
  subAccountId: string;
  hasEAuthAccess: boolean;
  isEAuthRestricted: boolean;
  destroy$ = new Subject<void>();
  transactionTypeLabel: string;
  extraPrompts: ItExtraPrompts | ChecksExtraPrompts | WireExtraPrompts;
  itClientAuthorizationNeeded: boolean;
  eAuthInfo: EAuthInfo;
  isPending$: Observable<boolean>;
  pendReasonDescription$: Observable<string>;
  eAuthInfo$: Observable<EAuthInfo>;
  eAuthCustomers$: Observable<Customers[]>;
  pendReasonIcon$: Observable<'warning' | 'success'>;
  submitAndCreateStatus$: Observable<SubmitAndCreateStatus>;
  transferType: string;
  submitAndCreateBannerText: string;
  displayAllianceWarning$: Observable<boolean>;
  sendAllianceInstructionsWarningText: string = eAuthNotifications.sendAllianceInstructionsFailedShort;
  digitalToPdfText: string = eAuthNotifications.digitalToPdf;
  oneTimeTotalDistWarningMessage: string = eAuthNotifications.submitPrefilledFormMessage;
  i401kConfirmationMessage: string = eAuthNotifications.submitPrefilledFormMessage;
  showSaveAsTemplate: boolean = false;
  maxLotsWarning: { displayWarning: boolean; } = { displayWarning: false };
  createTemplateCallSuccess: boolean;
  transactionType: string;
  isCreateRecurringAchProfile: boolean;
  isCreateOnRequestAchProfile: boolean;
  templateUsed: boolean;
  positionLotsPhase2EnabledFsg: boolean = false;
  toAccounts: string[] = [];
  toAccount: string;
  transferTotals: { receivingAccount: { accountNumber: string, accountName: string, accountType: string; }, positionTotatValue: string, cashTotalValue: string; }[] = [];
  transactionDetailsInfo: { receivingAccount: { accountNumber: string, accountName: string, accountType: string; }, contributionType: string, distributionReason: string; }[] = [];
  transactionDetailsInContext: { contributionType: string, distributionReason: string; };
  toAccountPTRSInfo: ItToAccountDetails[] | undefined;
  toAccountPTRSInfoInContext: { trustIncomeToPercent: string, trustPrincipalToPercent: string, trustDepositCode: string; };
  displayToAccPTRSFields: boolean;
  isCashAvailableInContext: boolean = false;
  showTransferTotalsCash: boolean = true;
  toAccountsInfoById: ToAccountInfoById;
  transactionFailedStatus: boolean;
  itsInfo: InternalTransfersInfo;
  requiringClientAuthorizationWarningText: string = eAuthNotifications.requiringClientAuthorizationWarningMessage;
  itAckListResponse: InternalTransferListAcknowledgeResponseById = {};
  itjournalVerifyResponse: InternalTransferVerifyResponse[] = [];
  viewButtonText = confirmationPageButtons.viewTransactionStatus;
  createTransactionButtonText = confirmationPageButtons.createAnotherTransaction;
  isInRFirm: boolean = false;
  authorizationFormPdf: string[];
  transactionForm: TransactionForm;
  canRelease: boolean = false;
  errorBannerTextPhase2: string = positionConstants.confirmationErrorBanner;
  genericServiceError = Constants.genericServiceError;
  isInitiator: boolean = false;
  hasReleaseAccess: boolean = false;
  zeroFedAndStateTax: boolean;
  fedTaxPercent: string;
  isInitiatorFlow: boolean = false;
  initiatorConfirmMessage: string = Constants.initiateInRTransaction;
  iTTotalDistWarningMessage: string = eAuthNotifications.digitalToPdf;
  isPendForRelease: boolean = false;
  useOriginalLayout: boolean = true;
  isAchTransaction: boolean = false;
  isCheckTransaction: boolean = false;
  isItTransaction: boolean = false;
  isCheckTransactionDigitalToPdfFlow: boolean = false;
  hasGoingDigitalFlowCheckCreateTxn: boolean = false;
  newRegTypesCheck = digitalRegTypes;
  regType: string;
  isOneTimeTotalDist: boolean = false;
  isChecksOneTimeTotalDist: boolean;
  isItOneTimeTotalDist: boolean = false;
  emvTotalDist: string;
  isChecksBundledOneTimeTotalDist: boolean;
  isUseStandingAuth: any;
  isSubmitAndCreate: boolean = false;
  isCreateSloaToggleFlagEnabled: boolean = false;
  standingAuthCanRelease: boolean = false;
  is401kToAccount: boolean;
  isNotInitReleaseFlowCustomRules: boolean = false;
  createTransactionButtonMultiple: string = confirmationPageButtons.createAnotherTransaction;
  viewButtonTextMultipleFlow = confirmationPageButtons.viewTransactionStatus;
  canShowIandRMessage = false;
  draftInContext: DraftListTableColumn;
  transmissionNotes: string = '';
  transmissionNotesLabel: string = transactionDetailLabels.wireTransmissionNotes;
  isCreatePdfFlow: boolean = false;
  displayAllianceWarning: boolean = false;

  constructor(private store: Store, private toastr: ToastrService, private ffs: FeatureFlagService, private currencyPipe: CurrencyPipe, private router: Router) { }

  ngOnInit(): void {
    this.requestSuccessful.emit();

    combineLatest([
      this.store.select(selectStagedTransactionType),
      this.store.select(selectIsEnableWiresCondensedLayout)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([transactionType, wiresCondensedFlaggedOn]) => {
      this.transactionType = transactionType;
      this.isCheckTransaction = this.transactionType === transactionTypes.check;
      this.isAchTransaction = this.transactionType === transactionTypes.ach;
      this.isItTransaction = this.transactionType === transactionTypes.internalTransfer;
      if (this.isCheckTransaction) {
        this.useOriginalLayout = false;
      }
      else if (this.isAchTransaction) {
        this.useOriginalLayout = false;
      }
      else if (this.isWireTransaction()) {
        this.useOriginalLayout = wiresCondensedFlaggedOn ? false : true;
      }
      else {
        this.useOriginalLayout = true;
      }
    });

    this.store.select(selectStagedUseTemplateVal).pipe(takeUntil(this.destroy$)).subscribe(useTemplateValue => this.templateUsed = useTemplateValue === 'yes');

    const templateUsed =
      this.transactionType === transactionTypes.wire
        ? this.templateUsed
          ? Template.templateUsed
          : Template.templateNotUsed
        : Template.templateUsedNA;
    AdobeHelpers.trackPage('confirm_transaction', { 'user_selection': templateUsed });

    this.store.pipe(
      takeUntil(this.destroy$),
      select(selectConfirmationEAuthInfo)
    ).subscribe(eAuthInfo => {
      this.hasEAuthAccess = eAuthInfo?.eauthAccess;
      this.isEAuthRestricted = eAuthInfo?.accountRestricted;
      this.itClientAuthorizationNeeded = eAuthInfo?.clientAuthorizationNeeded;
    });

    this.store.select(selectInternalTransfersInfo).pipe(takeUntil(this.destroy$)).subscribe((itsInfo) => {
      this.itsInfo = itsInfo;
      this.itAckListResponse = itsInfo.multipleAcknowledgeListById;
      this.itjournalVerifyResponse = itsInfo.multipleToJournalsList;
    });

    this.store.select(selectStagedTransactionForm).pipe(takeUntil(this.destroy$)).subscribe(form => {
      this.transactionForm = form;
      if (form?.transactionType && (isInternalTransferForm(form) || isCheckTransactionForm(form) || isWireForm(form))) {
        this.transactionTypeLabel = transactionTypesDict[form.transactionType];
        this.extraPrompts = form?.extraPrompts;
      }
      if ("toAccounts" in form.transactionDetails) {
        this.toAccountPTRSInfo = form?.transactionDetails?.toAccounts;
      }
      if ('frequency' in this.extraPrompts) {
        this.isOneTimeTotalDist = this.extraPrompts?.frequency === 'onetimeTotalDist';
        this.isChecksOneTimeTotalDist = this.isCheckTransaction && this.extraPrompts?.frequency === 'onetimeTotalDist';
      }
      if ('useBundledStandingAuth' in this.extraPrompts) {
        this.isChecksBundledOneTimeTotalDist = this.extraPrompts?.useBundledStandingAuth === 'onetimeTotalDist';
      }
      const wireTransactionDetails = form.transactionDetails as WireTransactionDetails;
      this.transmissionNotes = wireTransactionDetails?.transmissionNotes || '';
    });

    this.store.select(selectWireInfo).pipe(takeUntil(this.destroy$)).subscribe(wireInfo => {
      const isIrsAba = wireInfo?.isIrsAba || false;
      this.transmissionNotesLabel = isIrsAba ? transactionDetailLabels.wireIrsTransmissionNotes : transactionDetailLabels.wireTransmissionNotes;
    });

    this.store.select(selectPendForReleaseCustomRule).pipe(takeUntil(this.destroy$)).subscribe(noRuleReturned => {
      this.isNotInitReleaseFlowCustomRules = noRuleReturned;
    });

    this.eAuthInfo$ = this.store.select(selectEAuthInfo);
    this.store.select(selectEAuthInfo).pipe(takeUntil(this.destroy$)).subscribe(info => this.eAuthInfo = info);

    this.store.select(selectIsCreatePdfFlow).pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.isCreatePdfFlow = val;
    });

    this.store.select(selectIsOneTimeTotalDist).pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.isOneTimeTotalDist = val;
      this.isItOneTimeTotalDist = this.isItTransaction && this.isOneTimeTotalDist;
    });

    this.store.select(selectIs401kToAccount).pipe(takeUntil(this.destroy$)).subscribe(is401kToAccount => {
      this.is401kToAccount = is401kToAccount;
    });

    this.store.select(selectHasPositionLotsPhase2).pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.positionLotsPhase2EnabledFsg = val;
    });

    this.store.select(selectToAccountsInfo).pipe(takeUntil(this.destroy$)).subscribe(_toAccounts => {
      Object.keys(_toAccounts).forEach(toAccount => {
        this.toAccounts.push(toAccount);
      });
      this.toAccountsInfoById = _toAccounts;
    });

    combineLatest([
      this.store.select(selectHasAccessInitiateMoneyMovement),
      this.store.select(selectCanRelease),
      this.store.select(selectHasAccessReleaseMoneyMovement)
    ]).pipe(takeUntil(this.destroy$), filter(([hasInitiate, _]) => hasInitiate)).subscribe(([hasInitiate, canRelease, hasRelease]) => {
      this.isInitiator = true;
      this.hasReleaseAccess = hasRelease;
      this.isInitiatorFlow = hasInitiate && !hasRelease;
    });

    this.store.select(selectInitReleaseTransactionInContext).subscribe(value => {
      this.draftInContext = mapInitiatedDraftsToTableColumn(value);
    });

    this.store.select(selectMultipleToJournalsList).pipe(
      takeUntil(this.destroy$),
      withLatestFrom(this.store.select(selectMultipleToAccountsPositionLotTableData))
    ).subscribe(([VerifyResponseToAccountList, toPositionLotData]) => {
      VerifyResponseToAccountList.forEach(toAccountResponse => {
        this.transferTotals.push({
          receivingAccount: {
            accountNumber: toAccountResponse.toAccountInfo.accountNumber,
            accountName: toAccountResponse.toAccountInfo.accountRegistration[0] ?? '',
            accountType: toAccountResponse.toAccountInfo.regTypeShortName ?? ''
          },
          positionTotatValue: this.calculateSelectedPositionTotal(toAccountResponse.toAccountInfo.accountNumber),
          cashTotalValue: toAccountResponse.journalCashAmount
        });
        this.transactionDetailsInfo.push({
          receivingAccount: {
            accountNumber: toAccountResponse.toAccountInfo.accountNumber,
            accountName: toAccountResponse.toAccountInfo.accountRegistration[0] ?? '',
            accountType: toAccountResponse.toAccountInfo.regTypeShortName ?? ''
          },
          contributionType: toAccountResponse?.iraContributionType?.description ? this.camelCasePipe.transform(toAccountResponse?.iraContributionType?.description, true) : 'N/A',
          distributionReason: toAccountResponse?.iraFields?.distributionReason?.description ? this.camelCasePipe.transform(toAccountResponse?.iraFields?.distributionReason?.description) : 'N/A'
        });
      });

      if (this.is401kToAccount) {
        Object.keys(toPositionLotData).forEach((toAccount) => {
          const toAccountInfo = this.itsInfo?.toAccountInfo[toAccount];
          this.transferTotals.push({
            receivingAccount: {
              accountNumber: toAccountInfo.accountNumber,
              accountName: toAccountInfo.accountRegistration[0] ?? '',
              accountType: toAccountInfo.regTypeShortName ?? ''
            },
            positionTotatValue: this.calculateSelectedPositionTotal(toAccount),
            cashTotalValue: this.itsInfo?.totalCashAmount
          });
          this.transactionDetailsInfo.push({
            receivingAccount: {
              accountNumber: toAccountInfo.accountNumber,
              accountName: toAccountInfo.accountRegistration[0] ?? '',
              accountType: toAccountInfo.regTypeShortName ?? ''
            },
            contributionType: this.itsInfo?.toAccountPropertiesById[toAccount]?.iraContributionType.description ?? 'N/A',
            distributionReason: this.itsInfo?.toAccountPropertiesById[toAccount]?.inputIraFields.distributionReason.description ?? 'N/A',
          });
        });
      }
    });

    this.store.select(selectPendForRelease).pipe(takeUntil(this.destroy$)).subscribe(shouldPendForRelease => {
      this.isPendForRelease = shouldPendForRelease;
    });

    this.store.select(selectTransferType).pipe(
      takeUntil(this.destroy$)
    ).subscribe((transferType => {
      this.transferType = (typeof transferType === 'string') ? transferType : "";
      if (this.transferType === transferTypes.POSITIONS) {
        this.showTransferTotalsCash = false;
      }
    }));

    this.eAuthCustomers$ = this.store.select(selectEAuthCustomers);

    this.store.select(selectRegType).pipe(takeUntil(this.destroy$)).subscribe(regType => { this.regType = regType; });

    combineLatest([
      this.store.select(selectPdf),
      this.store.select(selectHasAccessInitiateMoneyMovement),
      this.store.select(selectCanRelease),
      this.store.select(selectIsSubmitAndCreate),
      this.store.select(selectIsCreateSloaToggleFlagEnabled),
      this.store.select(selectPendForReleaseCustomRule),
      this.store.select(selectStagedTransactionType),
      this.store.select(selectStandingAuthWireInfo)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([
      pdfData,
      hasInitiate,
      canRelease,
      isSubmitAndCreate,
      isCreateSloaToggleFlagEnabled,
      isNotInitReleaseFlowCustomRules,
      transactionType,
      standingAuthWireInfo
    ]) => {
      this.standingAuthCanRelease = standingAuthWireInfo?.authResponse?.canRelease ?? false;

      if (pdfData) {
        this.pdf = pdfData;
        let buffer = getArrayBufferFromBase64(pdfData);
        let pdf = new Blob([buffer], { type: 'application/pdf' });
        const isSplitCustomRulesWireSubmitAndCreateFlow = transactionType === transactionTypes.wire
          && hasInitiate
          && isSubmitAndCreate
          && isCreateSloaToggleFlagEnabled
          && this.standingAuthCanRelease
          && !isNotInitReleaseFlowCustomRules;

        if (
          !hasInitiate
          || canRelease
          || this.is401kToAccount
          || this.isOneTimeTotalDist
          || this.isChecksBundledOneTimeTotalDist
          || isSplitCustomRulesWireSubmitAndCreateFlow
        ) {
          openPdfInNewWindow(pdf, false);
        }
      }
    });

    this.store.select(selectPdfs).pipe(takeUntil(this.destroy$)).subscribe(multiplePdfData => {
      if (multiplePdfData && multiplePdfData.length > 0 && this.isPositionsTransaction()) {
        this.authorizationFormPdf = multiplePdfData;
        multiplePdfData.forEach(data => {
          let buffer = getArrayBufferFromBase64(data);
          let pdf = new Blob([buffer], { type: 'application/pdf' });
          openPdfInNewWindow(pdf, false);
        });
      }
    });

    this.store.pipe(
      takeUntil(this.destroy$),
      select(selectSubAccountId),
    ).subscribe(subAccountId => {
      this.subAccountId = subAccountId;
    });

    this.store.select(selectTransferType).pipe(takeUntil(this.destroy$)).subscribe(type => {
      this.transferType = (typeof type === 'string') ? type : "";
    });

    this.isPending$ = this.store.pipe(select(selectIsPending));
    this.pendReasonDescription$ = this.store.pipe(select(selectMappedPendReason));
    this.pendReasonIcon$ = this.pendReasonDescription$.pipe(
      map((description) => {
        if (
          description === Constants.eauthNeeded ||
          description === Constants.defaultPendDescription ||
          description === submitAndCreateConfirmationMessages.eauthNeeded ||
          description === Constants.newRecurringAchProfileConfirmation
        ) {
          return 'success';
        }

        return 'warning';
      })
    );

    this.submitAndCreateStatus$ = this.store.select(selectHasSubmitAndCreateError);

    this.submitAndCreateStatus$.pipe(takeUntil(this.destroy$)).subscribe((status) => {
      if (status.standingAuthFailed) {
        this.submitAndCreateBannerText = submitAndCreateConfirmationMessages.submitSuccessCreateFail;
      }

      this.transactionFailedStatus = status.transactionFailed;
      if (status.transactionFailed) {
        this.submitAndCreateBannerText = submitAndCreateConfirmationMessages.submitFailCreateSuccess;
      }
    });

    this.displayAllianceWarning$ = this.store.select(selectSendEmailErrorState);
    this.displayAllianceWarning$.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.displayAllianceWarning = value;
    });

    // Check if Standing Auth transaction for displaying authorization form
    this.store.select(selectIsUseStandingAuth).pipe(takeUntil(this.destroy$)).subscribe(isUseStandingAuth => {
      this.isUseStandingAuth = isUseStandingAuth;
    });

    this.store.select(selectIsSubmitAndCreate).pipe(takeUntil(this.destroy$)).subscribe(isSubmitAndCreate => {
      this.isSubmitAndCreate = isSubmitAndCreate;
    });

    this.store.select(selectIsCreateSloaToggleFlagEnabled).pipe(takeUntil(this.destroy$)).subscribe(flag => {
      this.isCreateSloaToggleFlagEnabled = flag;
    });

    combineLatest([
      this.store.select(selectCreateWireTemplateCallSuccessful),
      this.store.select(selectStagedTransactionType),
      this.store.select(selectIsUseStandingAuth),
      this.store.select(selectIsBundled)
    ]).pipe(
      takeUntil(this.destroy$),
      filter(
        ([createTemplateSucceeded, transactionType, isUseStandingAuth, isBundled]) =>
          transactionType === transactionTypes.wire
          && !createTemplateSucceeded && !isUseStandingAuth && !isBundled
      )
    ).subscribe(() => {
      this.showSaveAsTemplate = true;
    });

    combineLatest([
      this.store.select(selectIsCreateAchProfile),
      this.store.select(selectAchRecurringFrequency),
    ]).pipe(takeUntil(this.destroy$)).subscribe(([isCreateAchProfile, achRecurringFrequency]) => {
      this.isCreateRecurringAchProfile = isCreateAchProfile && !!achRecurringFrequency;
    });

    combineLatest([
      this.store.select(selectAchStandingAuthId),
      this.store.select(selectInstructions)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([achStandingAuthId, achInstructions]) => {
      //if the profile ID used to create the ACH transfer is not in the original ach instructions list,
      //this indicates a new on-request profile was created
      this.isCreateOnRequestAchProfile = !(achInstructions.some(instruction => instruction.cashieringRqstId === achStandingAuthId));
    });

    this.store.select(selectInternalTransfersInfo).pipe(takeUntil(this.destroy$)).subscribe((itsInfo) => {
      this.itsInfo = itsInfo;
      this.getDisplayToAccPTRSFields();
      Object.keys(itsInfo.toAccountInfo).forEach(toAccount => {
        this.emvTotalDist = this.currencyPipe.transform(String(itsInfo?.rowData[toAccount]?.estimatedMarketValueTotal ?? 0)) ?? '';
      });
    });

    combineLatest([
      this.store.select(selectHasAccessInitiateMoneyMovement),
      this.store.select(selectCanRelease),
    ]).pipe(takeUntil(this.destroy$)).subscribe(([hasInitiate, canRelease]) => {
      this.isInRFirm = hasInitiate;
      this.canRelease = canRelease;
      if (hasInitiate && !this.isOneTimeTotalDist) {
        if (!canRelease) {
          if (!this.isInitiateAndReleaseFlowFromTable) {
            this.viewButtonText = confirmationPageButtons.viewInR;
          }
          this.createTransactionButtonText = confirmationPageButtons.initiateAnotherTransaction;
        } else {
          if (this.isInitiateAndReleaseFlowFromTable) {
            this.viewButtonText = confirmationPageButtons.viewInR;
            this.viewButtonText = confirmationPageButtons.releaseAnotherTransaction;
          }
        }
      }
    });

    this.getZeroFederalAndStateTax();
    this.getFedTaxPercent();
    this.getTransactionDetailsInContext();
    this.getToAccountPTRSInfoInContext();
    // Scroll to top of the page on page load
    this.scrollToTop();
  }


  getCreateTransactionButtonText(toAccount: string) {
    toAccount = toAccount.replace('-', '');
    let isIandR = this.checkToAccountISIandR(toAccount);
    let pendForRelease = this.checkToAccountPendForRelease(toAccount);
    if (this.isInRFirm) {
      if (isIandR && pendForRelease) {
        this.createTransactionButtonMultiple = confirmationPageButtons.initiateAnotherTransaction;
      } else {
        this.createTransactionButtonMultiple = confirmationPageButtons.createAnotherTransaction;
      }
      return this.createTransactionButtonMultiple;
    }
    return this.createTransactionButtonText;
  }

  checkToAccountISIandR(id: string) {
    let hasRuleReturned = false;
    if (this.itsInfo?.journalAuthResponse[id]?.pendForReleaseReason == '') {
      hasRuleReturned = false;
    } else {
      hasRuleReturned = true;
    }
    return this.isInRFirm && hasRuleReturned;
  }

  checkToAccountPendForRelease(id: string) {
    let pendForRelease = false;
    if (this.itsInfo.journalAuthResponse[id]?.pendForRelease) {
      pendForRelease = true;
    } else {
      pendForRelease = false;
    }
    return this.isInRFirm && pendForRelease;

  }

  checkToAccountCanRelease(id: string) {
    return this.itsInfo.journalAuthResponse[id]?.canRelease;
  }

  getViewButtontextMultipleFlow(toAccount: string) {
    toAccount = toAccount?.replace('-', '');
    let isIandR = this.checkToAccountISIandR(toAccount ?? '');
    let pendForRelease = this.checkToAccountPendForRelease(toAccount);
    let canRelease = this.checkToAccountCanRelease(toAccount);
    if (this.isInRFirm) {
      if (isIandR && pendForRelease) {
        this.viewButtonTextMultipleFlow = confirmationPageButtons.viewInR;
      } else if (canRelease && this.isInitiateAndReleaseFlowFromTable) {
        this.viewButtonTextMultipleFlow = confirmationPageButtons.releaseAnotherTransaction;
      } else {
        this.viewButtonTextMultipleFlow = confirmationPageButtons.viewTransactionStatus;
      }

      return this.viewButtonTextMultipleFlow;
    }
    return this.viewButtonText;
  }

  showIandRmessageMultipleFlow(toAccount: string) {
    //As isItOneTimeTotalDist and is401kToAccount is create PDF flow always return false to hide IandR message
    if (this.isItOneTimeTotalDist || this.is401kToAccount) {
      return false;
    }
    toAccount = toAccount?.replace('-', '');
    let isIandR = this.checkToAccountISIandR(toAccount ?? '');
    let pendForRelease = this.checkToAccountPendForRelease(toAccount);

    if (this.isInRFirm) {
      if (isIandR && pendForRelease) {
        this.canShowIandRMessage = true;
      } else {
        this.canShowIandRMessage = false;
      }
    } else {
      this.canShowIandRMessage = this.isInitiator && !this.isInitiateAndReleaseFlowFromTable && this.isInRFirm;
    }
    return this.canShowIandRMessage;
  }

  ngAfterViewInit() {
    this.setActiveListeners();
  }

  // This function prevents the tabs Everest component from redirecting to input when clicking left or right scroll arrow
  preventDefault(event: any) {
    event.preventDefault();
  }

  getZeroFederalAndStateTax() {
    this.itjournalVerifyResponse.forEach(iraTransaction => {
      if (this.toAccountInContextDetails.toAccountNumber === iraTransaction.toAccountInfo.accountNumber) {
        if (iraTransaction.iraFields.distributionReason.code === '0 ' ||
          iraTransaction.iraFields.distributionReason.code === 'Q ') {
          this.zeroFedAndStateTax = true;
        } else {
          this.zeroFedAndStateTax = false;
        }
      }
    });
  }

  getFedTaxPercent() {
    this.itjournalVerifyResponse.forEach(iraTransaction => {
      if (this.toAccountInContextDetails.toAccountNumber === iraTransaction.toAccountInfo.accountNumber) {
        this.fedTaxPercent = iraTransaction.iraFields.fedTaxAmount;
    }});
  }

  downloadPdf(): void {
    setTimeout(() => {
      let temp = document.title;
      document.title = 'Move Money - Schwab Advisor Center';
      try {
        positionPage('print-confirmation-page');
        if (!document.execCommand('print', false, '')) {
          window.print();
        }
      } catch {
        positionPage('print-confirmation-page');
        window.print();
      }
      document.title = temp;
    }, 10);
  }

  getEauthSuccessMessage() {
    const ack = this.itAckListResponse[this.toAccountInContext];
    const pendReasons = Array.isArray(ack?.pendReasons) ? ack.pendReasons : [];

    if (pendReasons.includes(pendReasonsStrings?.clientAuthEauth)) {
      return Constants.eauthNeeded;
    }

    if (ack?.status?.description && ack.status.description !== "Approved" && pendReasons.length > 0) {
      if (pendReasons.includes(pendReasonsStrings?.ndfaLPFA) || pendReasons.includes(pendReasonsStrings?.clientAuthLOA) || pendReasons.includes(pendReasonsStrings?.taxElections)) {
        return '';
      } else {
        return Constants.defaultPendDescription;
      }
    }
  }

  calculateSelectedPositionTotal(toAccount: string): string {
    var totalAmount = 0;
    this.itsInfo.rowData[toAccount].positionData.forEach((position) => {
      if (position["Shares to move"] !== '' && position["Shares to move"] !== null) {
        totalAmount = totalAmount + parseFloat(position["Estimated market value"].replace(/[^\d.-]/g, ''));
      }
    });
    return this.currencyPipe.transform(totalAmount) ?? '$0';
  }

  sumTotals(amounts: string[]): string {
    var total = 0;
    amounts.forEach(amount => {
      total = total + Number(amount.replace(/[^\d.-]/g, ''));
    });
    return this.currencyPipe.transform(total) ?? '$0';
  }

  onViewTransactionStatusClicked(): void {
    if ((this.isInRFirm && this.canRelease && this.isInitiateAndReleaseFlowFromTable) || (this.isInRFirm && !this.canRelease && !this.isInitiateAndReleaseFlowFromTable)) {
      this.router.navigate([navRouteConstants.initiateAndRelease]);
    };
    this.store.dispatch(ConfirmationPageActions.viewTransactionStatusClicked({ accountId: this.subAccountId }));
  }

  onViewTransactionStatusClickedMultipleToAccount(toAccount: string): void {
    toAccount = toAccount?.replace('-', '');
    let pendForRelease = this.checkToAccountPendForRelease(toAccount);

    if (this.isInRFirm) {
      if (pendForRelease) {
        this.router.navigate([navRouteConstants.initiateAndRelease]);
      } else {
        this.store.dispatch(ConfirmationPageActions.viewTransactionStatusClicked({ accountId: this.subAccountId }));
      }
    } else {
      this.onViewTransactionStatusClicked();
    }
  }

  onCreateAnotherRequestClicked(): void {
    this.store.dispatch(ConfirmationPageActions.createAnotherRequestClicked({ accountId: this.subAccountId }));
    AdobeHelpers.fireClick(AdobeConstants.LINKID + AdobeConstants._CREATE_ANOTHER_REQUEST);
  }

  onSaveAsTemplateClicked(): void {
    this.saveAsTemplateClicked.emit(true);
  }

  isPositionsTransaction(): boolean {
    return this.transferType === transferTypes.POSITIONS || this.transferType === transferTypes.POSITIONS_CASH;
  }

  scrollToTop(): void {
    const elem = this.isInitiateAndReleaseFlowFromTable ? document.getElementById('initiate-and-release-content') : document.getElementById('create-request-container');
    setTimeout(function () {
      elem?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
    }, 0);
  }

  getOverAllErrorStatus() {
    let keys = Object.keys(this.itAckListResponse);
    let error = false;
    keys.forEach(key => {
      if (this.itAckListResponse[key].hasError) {
        error = true;
      }
    });
    return error;
  }

  getGenericServiceError(key: any) {
    if (this.itAckListResponse[key]?.hasError) {
      return this.genericServiceError;
    }
  }

  getErrorStatus(key: any) {
    return this.itAckListResponse[key]?.hasError;
  }

  onViewMultiAuthFormClicked(toAccount: string): void {
    let toAccounts: string[] = [];
    var itAckListResponseById = this.itAckListResponse[this.toAccountInContext];
    if (this.authorizationFormPdf?.length > 0) {
      for (let i = 0; i < this.itsInfo.multipleToJournalsList.length; i++) {
        let toAccountIncontext = this.itsInfo.multipleToJournalsList[i].toAccountInfo.accountNumber;
        if (
          !itAckListResponseById?.hasError
          && this.eAuthInfo.isOptedOut
          && (
            itAckListResponseById?.pendReasons?.includes(pendReasonsStrings?.clientAuthLOA)
            || itAckListResponseById?.pendReasons?.includes(pendReasonsStrings?.taxElections)
            || itAckListResponseById?.pendReasons?.includes(pendReasonsStrings?.ndfaLPFA)
          )
        ) {
          if (!this.itsInfo.multipleAcknowledgeListById[toAccountIncontext].hasError) {
            toAccounts.push(toAccountIncontext);
          }
        }
      }
    }
    
    //filter accounts that did not meet a rule and are hence not I&R to open the LOA
    if (this.isInRFirm && this.authorizationFormPdf?.length > 0 && !this.isOneTimeTotalDist) {
      let accountsNotIandR = [];
      let keys = Object.keys(this.itsInfo.journalAuthResponse);

      // setting accountsNotIandR to same order as toAccounts to avoid incorrect toaccount info in AuthForm
      accountsNotIandR = toAccounts
        .map(account => account.replace('-', ''))
        .filter(account => 
          keys.includes(account) && 
          this.itsInfo.journalAuthResponse[account].pendForReleaseReason === ''
        );

      for (let i = 0; i < accountsNotIandR.length; i++) {
        if (this.isOneTimeTotalDist || (toAccount.replace('-', '') === accountsNotIandR[i] && !this.itsInfo.toAccountPropertiesById[toAccount]?.hasError)) {
          let buffer = getArrayBufferFromBase64(this.authorizationFormPdf[i]);
          let pdf = new Blob([buffer], { type: 'application/pdf' });
          openPdfInNewWindow(pdf, false);
        }
      }
    } else {
      for (let i = 0; i < toAccounts.length; i++) {
        if (this.isOneTimeTotalDist || (toAccount === toAccounts[i] && !this.itsInfo.toAccountPropertiesById[toAccount]?.hasError)) {
          let buffer = getArrayBufferFromBase64(this.authorizationFormPdf[i]);
          let pdf = new Blob([buffer], { type: 'application/pdf' });
          openPdfInNewWindow(pdf, false);
        }
      }
    }

    if (this.is401kToAccount || this.isOneTimeTotalDist) {
      let buffer = getArrayBufferFromBase64(this.authorizationFormPdf[0]);
      let pdf = new Blob([buffer], { type: 'application/pdf' });
      openPdfInNewWindow(pdf, false);
    }
  }

  onViewAuthFormClicked(): void {
    if (this.pdf) {
      let buffer = getArrayBufferFromBase64(this.pdf);
      let pdf = new Blob([buffer], { type: 'application/pdf' });
      openPdfInNewWindow(pdf, false);
    }
  }

  getTransactionDetailsInContext() {
    this.transactionDetailsInfo.forEach(transactionDetail => {
      if (transactionDetail?.receivingAccount?.accountNumber === this.toAccountInContextDetails?.toAccountNumber) {
        this.transactionDetailsInContext = { contributionType: transactionDetail.contributionType, distributionReason: transactionDetail.distributionReason };
      }
    });
  }

  getToAccountPTRSInfoInContext() {
    if (this.toAccountPTRSInfo) {
      this.toAccountPTRSInfo.forEach(ptrsInfo => {
        if (ptrsInfo?.toAccount === this.toAccountInContextDetails?.toAccountNumber) {
          this.toAccountPTRSInfoInContext = { trustIncomeToPercent: ptrsInfo?.trustIncomeToPercent ?? '', trustPrincipalToPercent: ptrsInfo?.trustPrincipalToPercent ?? '', trustDepositCode: ptrsInfo?.trustDepositCode ?? '' };
        }
      });
    }
  }

  getDisplayToAccPTRSFields() {
    Object.keys(this.itsInfo.toAccountInfo).forEach((toAccount) => {
      if (toAccount === this.toAccountInContextDetails?.toAccountNumber) {
        this.isCashAvailable();
        this.displayToAccPTRSFields = this.itsInfo.toAccountInfo[toAccount].isTrust && this.transferType === transferTypes.POSITIONS_CASH && this.isCashAvailableInContext;
      }
    });
  }

  isCashAvailable() {
    if ("cashAmounts" in this.transactionForm.transactionDetails) {
      let cashAmounts = this.transactionForm.transactionDetails.cashAmounts ?? [];
      Object.keys(cashAmounts).forEach((index: any) => {
        if (cashAmounts[index]?.toAccount === this.toAccountInContextDetails?.toAccountNumber) {
          if (![undefined, null, "$0.00", ""].includes(cashAmounts[index]?.amount)) {
            this.isCashAvailableInContext = true;
          }
          else {
            this.isCashAvailableInContext = false;
          }
        }
      });
    }
  }

  isWireTransaction(): boolean {
    return this.transactionType === transactionTypes.wire;
  }

  getLoaNeededMessage(): string {
    if (this.eAuthInfo.isOptedOut) {
      var itAckListResponseById =
        this.itAckListResponse[this.toAccountInContext];
      var pendReasons = itAckListResponseById?.pendReasons;
      if (
        itAckListResponseById?.status?.description &&
        itAckListResponseById?.status?.description !== 'Approved' &&
        (pendReasons?.includes(pendReasonsStrings?.ndfaLPFA) ||
          pendReasons?.includes(pendReasonsStrings?.clientAuthLOA) ||
          pendReasons?.includes(pendReasonsStrings?.taxElections))
      ) {
        this.requiringClientAuthorizationWarningText = Constants.loaNeeded;
        return this.requiringClientAuthorizationWarningText;
      }
    }
    return '';
  }

  isFirstPartyAccounts(): Boolean {
    for (let i = 0; i < this.itsInfo?.multipleToJournalsList?.length; i++) {
      if (this.toAccountInContext === this.itsInfo?.multipleToJournalsList[i]?.toAccountInfo?.accountNumber) {
        if (this.itsInfo?.multipleToJournalsList[i]?.isThirdParty) {
          return false;
        }
        else {
          return true;
        }
      }
    }
    return false;
  }

  get customRuleIandRFlowOff(): boolean {
    return (this.isNotInitReleaseFlowCustomRules && !this.canRelease && this.isInRFirm);
  }

  get canViewAuthForm(): boolean {
    return this.eAuthInfo?.generateLoaPdf;
  }

  get createAnotherTransactionButtonText(): string {
    return this.isChecksOneTimeTotalDist || this.isChecksBundledOneTimeTotalDist ? confirmationPageButtons.returnToAccountStatusD2P : this.createTransactionButtonText;
  }

  get printAuthFormText(): string {
    return this.isChecksOneTimeTotalDist || this.isChecksBundledOneTimeTotalDist ? confirmationPageButtons.printAuthForm : confirmationPageButtons.printPdfAuthForm;
  }

  get showClientAuthorizationDetails(): boolean {
    if (this.isOneTimeTotalDist || this.isChecksBundledOneTimeTotalDist)
      return true;
    if (this.extraPrompts?.useStandingAuth !== yesNo.no || this.eAuthInfo.isOptedOut) {
      return false;
    }
    else {
      return this.hasEAuthAccess;
    }
  }

  get ShowClientDetailsEauthOptout(): boolean {
    return this.eAuthInfo?.isOptedOut ? true : this.useOriginalLayout;
  }

  get showAuthFormLink(): boolean {
    if (
      this.isChecksOneTimeTotalDist
      || this.isOneTimeTotalDist
      || this.isChecksBundledOneTimeTotalDist
      || this.is401kToAccount
      || this.isCondensedWireCreatePdfFlow
      || this.showSplitCustomRulesWireAuthFormLink
    ) return true;
    // Show auth form link for non-positions transactions (with I&R releasor checks when applicable)
    return this.isInRFirm
      ? (
        this.canViewAuthForm &&
        !this.isPositionsTransaction() &&
        (this.canRelease && (this.isInitiateAndReleaseFlowFromTable ? this.hasEAuthAccess : true))
      )
      : (
        this.canViewAuthForm && !this.isPositionsTransaction()
      );
  }

  get isCondensedWireCreatePdfFlow(): boolean {
    return this.isWireTransaction() && !this.useOriginalLayout && this.isCreatePdfFlow;
  }

  get isSplitCustomRulesWireSubmitAndCreateFlow(): boolean {
    return this.isWireTransaction()
      && this.isInRFirm
      && this.isSubmitAndCreate
      && this.isCreateSloaToggleFlagEnabled
      && !this.isNotInitReleaseFlowCustomRules;
  }

  get showSplitCustomRulesWireAuthFormLink(): boolean {
    return this.isSplitCustomRulesWireSubmitAndCreateFlow
      && this.standingAuthCanRelease
      && !this.isPositionsTransaction()
      && !!this.pdf;
  }

  get toAccountInContextName(): string {
    return this.toAccountsInfoById[this.toAccountInContext]?.accountRegistration[0] ?? "";
  }

  get toAccountInContextType(): string {
    return this.toAccountsInfoById[this.toAccountInContext]?.regTypeShortName ?? "";
  }

  get toAccountInContextDetails(): { toAccountNumber: string, toAccountName: string, toAccountType: string; } {
    return { toAccountNumber: this.toAccountInContext, toAccountName: this.toAccountInContextName, toAccountType: this.toAccountInContextType };
  }

  get shouldDisplayMultipleToAccounts() {
    return this.isPositionsTransaction() && (this.ffs.featureFlags.positionLotsPhase2Enabled || this.positionLotsPhase2EnabledFsg);
  }

  get TotalValues() {
    return this.transferTotals.find(toAccount => toAccount.receivingAccount.accountNumber === this.toAccountInContext);
  }

  get toAccountInContext(): string {
    return this.toAccount ?? this.toAccounts[0];
  }

  get isInitiateAndReleaseFlowFromTable(): boolean {
    return this.isInRFirm && !!this.draftInContext.DraftId;
  }

  get totalToReceiveVerbiage(): string {
    return 'Available upon completion of Internal Transfer';
  }

  get showAuthorizationFormLink(): boolean {
    return (
      this.is401kToAccount ||                  // true for i401k and Roth i401k to accounts
      (this.isItTransaction && this.isOneTimeTotalDist) || // true for IT one-time-total-distribution flow
      (
        this.eAuthInfo?.isOptedOut &&
        !!this.getLoaNeededMessage() &&
        !this.isUseStandingAuth &&
        !this.getErrorStatus(this.toAccountInContext) &&
        (this.isInitiateAndReleaseFlowFromTable ? this.hasEAuthAccess : true)
      )
    );
  }

  itDocusignWarningMessage(): string {
    return this.is401kToAccount ? this.i401kConfirmationMessage : this.iTTotalDistWarningMessage;
  }

  get showI401kOrTotalDistWarning(): boolean {
    return this.is401kToAccount || (!this.displayAllianceWarning && (this.isChecksOneTimeTotalDist || this.isChecksBundledOneTimeTotalDist));
  }

  ngOnDestroy(): void {
    this.destroy$?.next();
    this.destroy$?.unsubscribe();
  }

  private setActiveListeners(): void {
    const toAccountTabs = document.getElementById('to-account-tabs');
    toAccountTabs?.addEventListener(
      'sdpsSelect',
      ((event: Event) => {
        this.toAccount = (event as CustomEvent).detail;
        this.getTransactionDetailsInContext();
        this.getToAccountPTRSInfoInContext();
        this.getDisplayToAccPTRSFields();
        this.getZeroFederalAndStateTax();
        this.getFedTaxPercent();
      })
    );
  }
}
